import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Hotel, Sparkles } from "lucide-react";
import Navigation from "@/components/wedding/Navigation";
import Footer from "@/components/wedding/Footer";
import { hotels, HotelCard } from "@/data/hotels";

const LAKE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/2cf1c386-8bf6-4b35-9508-22a3e6aba20d/images/fc4deebb8ae22ce74de5cd89c340ca4b73cceea7bf162013f6df0b01cacabf5f.png";

export default function AccommodationPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    const prev = document.title;
    document.title = "Accommodation · Michelle & Niall · 4 June 2027";
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1A1818]">
      <Navigation />
      <main>
        {/* Hero banner */}
        <section className="relative h-[38vh] min-h-[300px] w-full overflow-hidden">
          <img
            src={LAKE_IMG}
            alt="Lough Corrib, Galway"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1818]/80 via-[#1A1818]/40 to-[#1A1818]/30" />
          <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-12 md:pb-16">
            <Link
              to="/"
              data-testid="accommodation-back-link"
              className="self-start inline-flex items-center gap-2 text-xs tracking-luxe uppercase text-[#FAF9F6] hover:text-[#A3C1DA] mb-auto pt-24 md:pt-28 group"
            >
              <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" strokeWidth={1.6} />
              Back to home
            </Link>
            <p className="text-[10px] tracking-luxe uppercase text-[#A3C1DA] mb-4">
              Accommodation
            </p>
            <h1 className="font-display font-light text-4xl md:text-6xl lg:text-7xl text-[#FAF9F6] leading-[0.95] max-w-3xl">
              A bed for
              <span className="italic"> the night.</span>
            </h1>
          </div>
        </section>

        {/* Intro band */}
        <section className="paper-texture py-16 md:py-20 px-6">
          <div className="max-w-3xl mx-auto">
            <p className="font-body text-[#4A4747] text-base md:text-lg leading-relaxed mb-6">
              Guests are very welcome to stay at Glenlo Abbey itself, subject to
              availability. If you'd prefer something a little more affordable,
              there are plenty of lovely options nearby — and as Galway city is
              only a short drive away, taxis between the city and the venue are
              easy to arrange.
            </p>
            <div
              data-testid="accommodation-tip"
              className="flex items-start gap-3 bg-[#FAF9F6] border-l-4 border-[#6A1E2D] p-5"
            >
              <Sparkles className="w-4 h-4 text-[#6A1E2D] mt-1 shrink-0" strokeWidth={1.6} />
              <p className="text-sm text-[#1A1818] italic leading-relaxed">
                <strong className="not-italic font-medium text-[#6A1E2D]">A quick heads-up:</strong>{" "}
                this is a June bank holiday weekend in early summer, so Galway gets very busy —
                we'd really recommend booking your stay as soon as you can.
              </p>
            </div>
          </div>
        </section>

        {/* Hotels grid */}
        <section className="bg-[#F3EFE9]/60 py-20 md:py-28 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-10">
              <Hotel className="w-6 h-6 text-[#6A1E2D]" strokeWidth={1.4} />
              <h2 className="font-display text-3xl md:text-5xl text-[#1A1818] font-light">
                Recommended places to stay
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {hotels.map((h) => (
                <HotelCard key={h.name} h={h} />
              ))}
            </div>

            <p className="text-xs text-[#4A4747] mt-8 italic">
              All distances and drive times are approximate.
            </p>
          </div>
        </section>

        {/* CTA back to RSVP */}
        <section className="bg-[#6A1E2D] text-[#FAF9F6] py-16 md:py-20 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[10px] tracking-luxe uppercase text-[#A3C1DA] mb-4">
              Got your spot sorted?
            </p>
            <h3 className="font-display text-3xl md:text-5xl font-light leading-[1.05] mb-8">
              Let us know you're coming.
            </h3>
            <Link
              to="/rsvp"
              data-testid="accommodation-to-rsvp"
              className="inline-flex items-center gap-3 bg-[#FAF9F6] text-[#6A1E2D] hover:bg-[#A3C1DA] hover:text-[#1A1818] transition-colors duration-300 px-10 py-4 text-xs tracking-luxe uppercase"
            >
              RSVP →
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
